// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { LocalShipping as LocalShippingIcon } from '@/constants/hardfin-standard-icons';
import { Shipment } from '@/shipment/api/types';
import {
  canCancel,
  canMarkDelivered,
  canMarkReceived,
  canMarkShipped,
} from '@/shipment/utilities/shipment-actions';
import { Button, Stack } from '@mui/material';

interface HeaderActionsProps {
  shipment: Shipment;
  onEditShipment: () => void;
  onMarkShipped?: () => void;
  onMarkDelivered: () => void;
  onMarkReceived?: () => void;
  onCancel: () => void;
}

/**
 * Component that displays action buttons in the shipment detail header.
 * Each button appears only when the backend gate would accept the action. A
 * cancelled shipment keeps its edit button; the dialog locks what it cannot change.
 */
export function ShipmentHeaderActions(props: HeaderActionsProps) {
  const showMarkShipped = canMarkShipped(props.shipment) && props.onMarkShipped;
  const showMarkDelivered = canMarkDelivered(props.shipment);
  const showMarkReceived =
    canMarkReceived(props.shipment) && props.onMarkReceived;
  const showCancel = canCancel(props.shipment);

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {/* Edit shipment details button */}
      <Button variant="outlined" size="medium" onClick={props.onEditShipment}>
        Edit shipment details
      </Button>

      {/* Mark Shipped button */}
      {showMarkShipped && (
        <Button
          variant="contained"
          size="medium"
          startIcon={<LocalShippingIcon />}
          onClick={props.onMarkShipped}
        >
          Mark shipped
        </Button>
      )}

      {/* Mark Delivered button */}
      {showMarkDelivered && (
        <Button
          variant="contained"
          size="medium"
          onClick={props.onMarkDelivered}
        >
          Mark delivered
        </Button>
      )}

      {/* Mark Received button (inbound only) */}
      {showMarkReceived && (
        <Button
          variant="contained"
          size="medium"
          onClick={props.onMarkReceived}
        >
          Mark received
        </Button>
      )}

      {/* Cancel button */}
      {showCancel && (
        <Button
          variant="contained"
          size="medium"
          color="error"
          onClick={props.onCancel}
        >
          Cancel
        </Button>
      )}
    </Stack>
  );
}
